/**
 * AVSDetectionHealthBanner — iter173b
 *
 * Zeigt den Livestatus der AVS-Body-Detection (Gemini) im Admin-Dashboard.
 * Pollt alle 15s. Wenn Detection fehlgeschlagen ist → roter Alarm-Banner.
 * Wenn Detection wieder OK ist → grüner Bestätigungs-Banner mit „Wieder verfügbar seit …".
 */
import { useEffect, useState } from "react";
import { AlertOctagon, ShieldCheck, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 15000;

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return `vor ${diffSec}s`;
    if (diffSec < 3600) return `vor ${Math.round(diffSec / 60)}min`;
    if (diffSec < 86400) return `vor ${Math.round(diffSec / 3600)}h`;
    return d.toLocaleString();
  } catch { return iso; }
}

export default function AVSDetectionHealthBanner() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/avs-detection/status");
      setStatus(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, []);

  if (!status) {
    return (
      <div data-testid="avs-detection-health-loading"
        className="border border-[#E5E5E5] p-3 text-xs text-[#666]">
        AVS-Detection-Status wird geladen …
      </div>
    );
  }

  const healthy = !!status.is_healthy;
  return (
    <div data-testid="avs-detection-health-banner"
      className={`border p-3 flex items-start gap-3 ${
        healthy
          ? "border-emerald-400 bg-emerald-50"
          : "border-red-500 bg-red-50 animate-pulse"
      }`}
    >
      <div className="mt-0.5">
        {healthy
          ? <ShieldCheck size={18} className="text-emerald-700" />
          : <AlertOctagon size={18} className="text-red-700" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm font-bold ${healthy ? "text-emerald-800" : "text-red-800"}`}
            data-testid="avs-detection-health-status">
            AVS-Body-Detection: {healthy ? "verfügbar" : "AUSGEFALLEN"}
          </p>
          <button data-testid="avs-detection-health-refresh"
            onClick={load} disabled={loading}
            className="h-6 px-2 text-[10px] flex items-center gap-1 border border-current opacity-70 hover:opacity-100">
            <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> refresh
          </button>
        </div>
        <div className={`text-[11px] mt-1 ${healthy ? "text-emerald-700" : "text-red-700"}`}
          data-testid="avs-detection-health-detail">
          {healthy
            ? <>Letzter erfolgreicher Lauf: <b>{fmtRelative(status.last_ok_at)}</b>. Uploads erlauben AVS-Prüfung.</>
            : <>
                Letzter Fehler: <b>{fmtRelative(status.last_failure_at)}</b> · Grund:{" "}
                <code className="bg-white/60 px-1 rounded">{status.last_failure_reason || "unbekannt"}</code>.
                <br />
                Alle Bild-Uploads werden aktuell blockiert. Fehler wird automatisch erkannt und Status auf grün gesetzt, sobald Detection wieder läuft.
              </>
          }
        </div>
        {status.active_alerts_count > 0 && (
          <p className="text-[11px] mt-1 text-red-700" data-testid="avs-detection-health-alerts-count">
            Offene Alerts: <b>{status.active_alerts_count}</b>
          </p>
        )}
      </div>
    </div>
  );
}
